import React, { useMemo } from 'react'
import * as Modal from '_components/modal/generic-modal'
import { useFormContext, useWatch } from 'react-hook-form'
import { formatCurrency } from '_/utils/helpers'
import { getLaborValues } from '_/views/inspection-budget/utils/functions'
import { useInspectionLoading } from '_/views/inspection-budget/hooks/use-inspection-loading'
import useStyles from './styles'

const formatTime = value => `${(Number(value) || 0).toFixed(2).replace('.', ',')} horas`
const formatTimeShort = value => `${(Number(value) || 0).toFixed(2).replace('.', ',')} h`
const asNumber = (...values) => {
  const found = values.find(value => value !== undefined && value !== null && value !== '')
  return Number(found) || 0
}

const formatPercentage = value => `${(Number(value) || 0).toFixed(1).replace('.', ',')}%`

const getGroupFinalLaborValue = rows =>
  (rows ?? []).reduce((sum, row) => sum + (Number(row?.laborValue) || 0), 0)

const formatDailyAndPeriod = ({ dailyValue, periodValue }) => {
  const daily = Number(dailyValue) || 0
  const period = Number(periodValue) || 0
  const parts = []

  if (daily > 0) {
    parts.push(`${daily} ${daily === 1 ? 'diária' : 'diárias'}`)
  }

  if (period > 0) {
    parts.push(`${period} ${period === 1 ? 'período' : 'períodos'}`)
  }

  if (parts.length === 0) return '0 diária'
  if (parts.length === 1) return parts[0]
  return `${parts[0]} e ${parts[1]}`
}

const normalizePaintingFactor = value => {
  if (value === undefined || value === null || value === '') return null

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return null

  return numericValue > 1 ? numericValue / 100 : numericValue
}

const formatPaintingFactor = value => {
  const factor = normalizePaintingFactor(value)

  if (factor === null) return null

  return `${Math.max(0, factor * 100).toFixed(0)}%`
}

const getPaintingAppliedLabel = ({ hasValidPainting, paintingFactor, laborType }) => {
  const directFactorLabel = formatPaintingFactor(paintingFactor)

  if (directFactorLabel) return directFactorLabel

  if (!hasValidPainting || laborType?.slug === 'painting') return 'N/A'

  return formatPaintingFactor(laborType?.paintingAppliedPercentage) ?? 'N/A'
}

const getReferenceLabel = group => {
  const daily = Number(group?.dailyIdealValue) || 0
  const period = Number(group?.periodIdealValue) || 0
  return `Diária = ${formatCurrency(daily)} / Período = ${formatCurrency(period)}`
}

export function MemoryCalculationModal({ openModal, setOpenModal }) {
  const styles = useStyles()
  const { budget } = useInspectionLoading()
  const { control } = useFormContext()
  const compartments = useWatch({ control, name: 'inspectionBudgetCompartments' }) ?? []
  const laborValueTotal = useWatch({ control, name: 'laborValueTotal' })
  const calcMemoryData = budget?.calcMemoryData ?? []

  const memoryData = useMemo(() => {
    const serviceReferenceMap = new Map()
    const laborTypeReferenceMap = new Map()
    const resolutionReferenceMap = new Map()
    compartments.forEach((compartment, compartmentIndex) => {
      ;(compartment?.inspectionBudgetResolutions ?? []).forEach((resolution, resolutionIndex) => {
        const service = resolution?.service
        const laborType = service?.laborType
        const serviceId = service?.id
        const laborTypeId = laborType?.id
        const compartmentOrder = Number(compartment?.order) || compartmentIndex + 1
        const resolutionOrder = Number(resolution?.order) || resolutionIndex + 1
        const dailyIdeal = asNumber(laborType?.dailyIdeal, laborType?.daily_ideal)
        const periodIdeal = asNumber(laborType?.periodIdeal, laborType?.period_ideal)
        const serviceDescription = resolution?.serviceDescription ?? '-'

        if (serviceId && !serviceReferenceMap.has(serviceId)) {
          serviceReferenceMap.set(serviceId, {
            dailyIdeal,
            periodIdeal,
            laborTypeId,
            slug: laborType?.slug,
            paintingAppliedPercentage: laborType?.paintingAppliedPercentage,
          })
        }
        if (laborTypeId && !laborTypeReferenceMap.has(laborTypeId)) {
          laborTypeReferenceMap.set(laborTypeId, {
            dailyIdeal,
            periodIdeal,
            slug: laborType?.slug,
            paintingAppliedPercentage: laborType?.paintingAppliedPercentage,
          })
        }
        resolutionReferenceMap.set(`${compartmentOrder}:${resolutionOrder}`, {
          environmentName: compartment?.compartment || '-',
          serviceDescription,
        })
      })
    })

    if (calcMemoryData.length > 0) {
      const hasValidPainting = calcMemoryData.some(
        item =>
          item?.calculationData?.hasValidPainting ?? item?.calculation_data?.has_valid_painting
      )

      const groupsMap = calcMemoryData.reduce((acc, item, index) => {
        const calculationData = item?.calculationData ?? item?.calculation_data ?? {}
        const laborType = calculationData?.laborType ?? calculationData?.labor_type ?? {}
        const grouped =
          calculationData?.groupedByLaborType ?? calculationData?.grouped_by_labor_type ?? {}
        const resolution = calculationData?.resolution ?? {}
        const service = calculationData?.service ?? {}
        const compartmentOrder = item?.compartmentOrder ?? item?.compartment_order ?? 0
        const resolutionOrder = item?.resolutionOrder ?? item?.resolution_order ?? 0
        const groupKey = laborType?.id ?? `group-${index}`
        const serviceRef = serviceReferenceMap.get(service?.id)
        const laborTypeRef = laborTypeReferenceMap.get(laborType?.id) ?? serviceRef
        const normalizedLaborType = { ...laborTypeRef, ...laborType }
        const resolutionRef = resolutionReferenceMap.get(`${compartmentOrder}:${resolutionOrder}`)

        if (!acc[groupKey]) {
          acc[groupKey] = {
            laborType: normalizedLaborType,
            totalMO: asNumber(grouped?.totalMo, grouped?.total_mo),
            dailyValue: asNumber(grouped?.dailyValue, grouped?.daily_value),
            periodValue: asNumber(grouped?.periodValue, grouped?.period_value),
            totalMoValue: asNumber(grouped?.totalMoValue, grouped?.total_mo_value),
            dailyIdealValue: asNumber(
              grouped?.dailyIdealValue,
              grouped?.daily_ideal_value,
              laborType?.dailyIdeal,
              laborType?.daily_ideal,
              laborTypeRef?.dailyIdeal
            ),
            periodIdealValue: asNumber(
              grouped?.periodIdealValue,
              grouped?.period_ideal_value,
              laborType?.periodIdeal,
              laborType?.period_ideal,
              laborTypeRef?.periodIdeal
            ),
            rows: [],
          }
        }

        acc[groupKey].rows.push({
          id: `${compartmentOrder}-${resolutionOrder}-${service?.id ?? index}`,
          environmentName: resolutionRef?.environmentName || '-',
          serviceDescription: resolutionRef?.serviceDescription || service?.name || '-',
          laborTime: asNumber(resolution?.laborTime, resolution?.labor_time),
          coefficient: asNumber(resolution?.coefficientPercent, resolution?.coefficient_percent),
          laborValue: asNumber(resolution?.laborValue, resolution?.labor_value),
          paintingAppliedLabel: getPaintingAppliedLabel({
            hasValidPainting,
            laborType: normalizedLaborType,
            paintingFactor:
              resolution?.paintingReductionFactor ?? resolution?.painting_reduction_factor,
          }),
        })

        return acc
      }, {})

      const groups = Object.values(groupsMap).map(group => ({
        ...group,
        totalMoValue: getGroupFinalLaborValue(group?.rows),
      }))
      const totalHours = groups.reduce((sum, group) => sum + (Number(group?.totalMO) || 0), 0)
      const totalMoValue = groups.reduce(
        (sum, group) => sum + (Number(group?.totalMoValue) || 0),
        0
      )
      return {
        totalHours,
        totalMoValue,
        hasValidPainting,
        groups,
      }
    }

    const resolutions = compartments.flatMap(compartment =>
      (compartment?.inspectionBudgetResolutions?.filter(Boolean) ?? []).map(resolution => ({
        ...resolution,
        environmentName: compartment?.compartment || '-',
      }))
    )

    const { laborGroups, hasValidPainting } = getLaborValues(resolutions)
    const totalHours = resolutions.reduce(
      (sum, resolution) => sum + (Number(resolution?.laborTime) || 0),
      0
    )
    const groupedResolutions = resolutions.reduce((acc, resolution) => {
      const laborType = resolution?.service?.laborType
      const key = laborType?.id

      if (!key || !laborGroups[key]) return acc

      if (!acc[key]) acc[key] = []
      acc[key].push(resolution)
      return acc
    }, {})

    const groups = Object.values(laborGroups).map(group => {
      const rows = (groupedResolutions[group?.laborType?.id] ?? []).map((resolution, index) => {
        const laborTime = Number(resolution?.laborTime) || 0
        const coefficient = group?.totalMO ? (laborTime / group.totalMO) * 100 : 0

        return {
          id: resolution?.id ?? `${group?.laborType?.id}-${index}`,
          environmentName: resolution?.environmentName || '-',
          serviceDescription: resolution?.serviceDescription ?? '-',
          laborTime,
          coefficient,
          laborValue: Number(resolution?.laborValue) || 0,
          paintingAppliedLabel: getPaintingAppliedLabel({
            hasValidPainting,
            laborType: resolution?.service?.laborType,
          }),
        }
      })

      return {
        ...group,
        dailyIdealValue: asNumber(group?.laborType?.dailyIdeal, group?.laborType?.daily_ideal),
        periodIdealValue: asNumber(group?.laborType?.periodIdeal, group?.laborType?.period_ideal),
        totalMoValue: getGroupFinalLaborValue(rows),
        rows,
      }
    })

    const totalMoValue =
      Number(laborValueTotal) ||
      resolutions.reduce((sum, resolution) => sum + (Number(resolution?.laborValue) || 0), 0)

    return {
      totalHours,
      totalMoValue,
      hasValidPainting,
      groups,
    }
  }, [calcMemoryData, compartments, laborValueTotal])

  return (
    <Modal.Root
      className={styles.root}
      open={openModal}
      onOpenChange={setOpenModal}
      onClose={() => setOpenModal(false)}
    >
      <Modal.TitleModal title="Memória de Cálculo" />

      <Modal.Content className={styles.content}>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Tempo total</span>
            <span className={styles.summaryValue}>{formatTime(memoryData.totalHours)}</span>
          </div>

          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Flag pintura</span>
            <span className={styles.flagValue}>
              <span
                className={`${styles.dot} ${
                  memoryData.hasValidPainting ? styles.dotActive : styles.dotInactive
                }`}
              />
              {memoryData.hasValidPainting ? 'Ativo' : 'Inativo'}
            </span>
          </div>

          <div className={`${styles.summaryCard} ${styles.summaryCardPrimary}`}>
            <span className={styles.summaryLabelPrimary}>Valor total MO</span>
            <span className={styles.summaryValuePrimary}>
              {formatCurrency(memoryData.totalMoValue)}
            </span>
          </div>
        </div>

        <div className={styles.groupsContainer}>
          {memoryData.groups.length === 0 && (
            <div className={styles.emptyState}>
              Sem serviços para demonstrar memória de cálculo.
            </div>
          )}
          {memoryData.groups.map(group => (
            <div key={group?.laborType?.id} className={styles.groupCard}>
              <div className={styles.groupHeader}>
                <div className={styles.groupInfo}>
                  <span className={styles.groupTitle}>{group?.laborType?.name || '-'}</span>
                  <span className={styles.groupMeta}>
                    {`${formatTime(group?.totalMO)} [${formatDailyAndPeriod({
                      dailyValue: group?.dailyValue,
                      periodValue: group?.periodValue,
                    })}] | ${getReferenceLabel(group)}`}
                  </span>
                </div>
                <span className={styles.groupTotal}>{`TOTAL MÃO DE OBRA ${formatCurrency(
                  group?.totalMoValue || 0
                )}`}</span>
              </div>

              <div className={styles.tableHeader}>
                <span>Ambiente</span>
                <span>Descrição de Serviço</span>
                <span>Tempo</span>
                <span>Coeficiente (%)</span>
                <span>% com Pintura</span>
                <span>Valor final</span>
              </div>

              {group.rows.map(row => (
                <div key={row.id} className={styles.tableRow}>
                  <span>{row.environmentName}</span>
                  <span>{row.serviceDescription}</span>
                  <span>{formatTimeShort(row.laborTime)}</span>
                  <span>{formatPercentage(row.coefficient)}</span>
                  <span>{row.paintingAppliedLabel}</span>
                  <span className={styles.rowValue}>{formatCurrency(row.laborValue)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Modal.Content>

      <Modal.Actions>
        <Modal.ButtonFullBlue onClick={() => setOpenModal(false)}>Fechar</Modal.ButtonFullBlue>
      </Modal.Actions>
    </Modal.Root>
  )
}
